import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Appointment } from '../../domain/models/appointment.model';
import { AppointmentCreateInput } from '../../domain/models/appointment-create-input.model';
import { AppointmentListQuery } from '../../domain/models/appointment-list-query.model';
import { AppointmentListResult } from '../../domain/models/appointment-list-result.model';
import { Department } from '../../domain/models/department.model';
import { IAppointmentsRepository } from '../../domain/repositories/appointments.repository';
import { Professional } from '../../domain/models/professional.model';
import { ReasonType } from '../../domain/models/reason-type.model';
import { Shift } from '../../domain/models/shift.model';
import { AppointmentsRemoteDatasource } from '../datasources/remote/appointments.remote-datasource';
import { AppointmentMapper } from '../mappers/appointment.mapper';

const DEFAULT_PAGE_SIZE = 200;

@Injectable()
export class AppointmentsRepositoryImpl implements IAppointmentsRepository {
  private readonly datasource = inject(AppointmentsRemoteDatasource);
  private readonly mapper = inject(AppointmentMapper);

  public list(query: AppointmentListQuery): Observable<AppointmentListResult> {
    return this.datasource
      .list({
        startFrom: query.startFrom,
        startTo: query.startTo,
        clientId: query.clientId,
        patientId: query.patientId,
        departmentId: query.departmentId,
        page: query.page ?? 1,
        pageSize: query.pageSize ?? DEFAULT_PAGE_SIZE,
      })
      .pipe(
        map((dto) => ({
          items: dto.Items.map((item) => this.mapper.toAppointment(item)),
          total: dto.TotalCount,
          asOfUtc: dto.AsOfUtc ?? null,
        })),
      );
  }

  public getById(id: string): Observable<Appointment> {
    return this.datasource.getById(id).pipe(map((dto) => this.mapper.toAppointment(dto)));
  }

  public create(input: AppointmentCreateInput): Observable<Appointment> {
    const payload = this.mapper.toCreatePayload(input);
    return this.datasource.create(payload).pipe(map((dto) => this.mapper.toAppointment(dto)));
  }

  public update(id: string, input: AppointmentCreateInput): Observable<Appointment> {
    const payload = this.mapper.toUpdatePayload(input);
    return this.datasource.update(id, payload).pipe(map((dto) => this.mapper.toAppointment(dto)));
  }

  public cancel(id: string): Observable<void> {
    return this.datasource.delete(id);
  }

  public listProfessionals(): Observable<readonly Professional[]> {
    return this.datasource.listProfessionals().pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toProfessional(item))),
    );
  }

  public listReasonTypes(): Observable<readonly ReasonType[]> {
    return this.datasource.listReasonTypes().pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toReasonType(item))),
    );
  }

  public listDepartments(organizationId: number): Observable<readonly Department[]> {
    return this.datasource.listDepartments(organizationId).pipe(
      map((items) => items
        .map((item) => this.mapper.toDepartment(item))
        .filter((department): department is Department => department !== null)),
    );
  }

  public getProfessionalShifts(from: string, to: string): Observable<readonly Shift[]> {
    return this.datasource.getProfessionalShifts(from, to).pipe(
      map((dto) => dto.Items.map((item) => this.mapper.toShift(item)).filter((shift) => shift !== null)),
    );
  }
}
